import 'swiper/css'

import classNames from 'classnames'
import gsap from 'gsap'
import * as React from 'react'
import {Swiper, SwiperSlide, useSwiper} from 'swiper/react'

import IconArrowLeft from '@/assets/specify/IconArrowLeft'
import IconArrowRight from '@/assets/specify/IconArrowRight'
import {makeEditable} from '@/common/utils'
import Button from '@/components/Button'
import Buttons from '@/components/Buttons'
import Content from '@/components/Content'
import {Grid} from '@/components/Grid'
import Heading from '@/components/Heading'
import Image from '@/components/Image'
import Kicker from '@/components/Kicker'
import Link from '@/components/Link'

const TeamSlider = ({id, headingTag, kicker, heading, content, buttons, items, _editable}) => {
  items = items || []

  const HeadingTag = headingTag || 'p'

  const swiperRef = React.useRef()

  const [progress, setProgress] = React.useState(0)

  return (
    <section
      id={id ? id : undefined}
      {...makeEditable(_editable)}
      className='py-10 md:py-20 lg:py-31'
    >
      <Grid className='gap-y-18'>
        <HeadingTag className='col-span-full flex flex-col items-center gap-4 text-center md:col-span-4 md:col-start-2 lg:col-span-6 lg:items-start lg:text-left'>
          <Kicker kicker={kicker} />
          <Heading heading={heading} />
        </HeadingTag>

        <div className='col-span-full flex flex-col items-center gap-6 text-center md:col-span-4 md:col-start-2 lg:col-span-4 lg:col-start-9 lg:items-start lg:self-center lg:text-left'>
          <Content size='m-medium' content={content} />
          <Buttons
            buttons={buttons}
            className='flex flex-col items-center gap-4 lg:flex-row lg:flex-wrap lg:justify-start'
          />
        </div>
      </Grid>

      <Swiper
        ref={swiperRef}
        spaceBetween={16}
        slidesPerView={1.5}
        centeredSlides
        allowTouchMove={false}
        breakpoints={{
          768: {slidesPerView: 2.3, spaceBetween: 32},
          1024: {
            slidesPerView: 4.15,
            centeredSlides: false,
            slidesOffsetBefore: 32,
            slidesOffsetAfter: 32,
          },
        }}
        className='mt-18 lg:mt-24'
        onSlideChange={(swiper) => setProgress(swiper.progress)}
      >
        {items.map((item, index) => {
          const background = gsap.utils.wrap(
            ['bg-turquoise-400', 'bg-violet-400', 'bg-yellow-400'],
            index
          )

          return (
            <SwiperSlide key={item._uid}>
              <Link
                link={item.link}
                className={classNames(
                  background,
                  'group flex flex-col gap-4 rounded-3xl p-2 pt-6 focus-visible:-outline-offset-2 focus-visible:outline-violey-700 md:gap-8 md:px-4 md:py-6'
                )}
              >
                {item.picture && (
                  <div className='mx-auto w-30 -rotate-6 transition group-hover:-rotate-3 group-focus-visible:-rotate-3 md:w-45'>
                    <div className='aspect-w-1 aspect-h-1'>
                      <Image
                        {...item.picture}
                        className='rounded-3xl'
                        ratio='1:1'
                        sizes={{sm: 140, md: 180, unit: 'px'}}
                      />
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='92'
                        height='81'
                        fill='none'
                        viewBox='0 0 92 81'
                        className='invisible left-[unset] top-full right-0 w-25 -translate-y-1/2 translate-x-1/4 scale-75 transition duration-200 group-hover:visible group-hover:scale-100 group-focus-visible:visible group-focus-visible:scale-100'
                      >
                        <path
                          className='fill-violet-600'
                          fillRule='evenodd'
                          d='M63.18 60.51a.96.96 0 0 1-1.33-.27L49.92 42.02a.96.96 0 0 1 1.6-1.05l11.94 18.22a.96.96 0 0 1-.28 1.32Zm-34.1 19.8a.96.96 0 0 1-1.24-.53 13.6 13.6 0 0 1-.64-3.73 79.71 79.71 0 0 1-.2-5.45c-.01-3.94.14-8.23.28-10.98a.96.96 0 1 1 1.92.1c-.15 2.72-.3 6.97-.28 10.87.01 1.95.07 3.8.18 5.31.13 1.56.3 2.65.51 3.16a.96.96 0 0 1-.52 1.25ZM11.97 53.4a.96.96 0 0 0-1.47-1.23L.24 64.56a.96.96 0 0 0 1.48 1.22l10.24-12.37ZM91.81 1.74a.96.96 0 0 1-1.24.54 8.51 8.51 0 0 0-1.13.02c-.72.06-1.61.18-2.58.34a102 102 0 0 0-5.5 1.08.96.96 0 0 1-.42-1.87c1.42-.32 3.63-.78 5.6-1.1 1-.16 1.95-.3 2.73-.36.4-.03.76-.05 1.07-.04.28 0 .63.03.93.15.5.2.73.75.54 1.24Zm-7.1 28.64a.96.96 0 0 0 .5-1.85L69.54 24.3a.96.96 0 0 0-.5 1.85l15.67 4.24Z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </div>
                  </div>
                )}
                <p className='flex flex-col gap-[-0.25rem] text-center md:gap-2'>
                  <span className='heading-xxs-millik block text-purple-900'>{item.name}</span>
                  <span className='body-s-medium block text-violet-600'>{item.job}</span>
                </p>

                <Button
                  as='span'
                  theme='secondary'
                  size='small'
                  label={item.buttonLabel}
                  className='mx-auto'
                />
              </Link>
            </SwiperSlide>
          )
        })}

        <Navigation progress={progress} />
      </Swiper>
    </section>
  )
}

const Navigation = ({progress}) => {
  const swiper = useSwiper()

  const className = [
    'flex h-14 w-14 items-center justify-center rounded-full border transition duration-300',
    '[&:not(:disabled)]:hover:text-white [&:not(:disabled)]:hover:bg-purple-900',
    'focus-visible:outline-violey-700',
    'disabled:border-purple-300 disabled:text-purple-300',
  ].join(' ')

  return (
    <div className='mt-6 flex justify-center gap-4'>
      <button onClick={() => swiper.slidePrev()} className={className} disabled={progress === 0}>
        <IconArrowLeft />
      </button>
      <button onClick={() => swiper.slideNext()} className={className} disabled={progress >= 0.9}>
        <IconArrowRight />
      </button>
    </div>
  )
}
export default TeamSlider
